import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import GeospatialMap from './components/GeospatialMap';
import AccessDenied from './components/AccessDenied';
import { CommunityLocation } from './types/api';
import { geoAPI, TokenValidationError } from './services/api';
import { Refresh, Warning, Analytics } from '@mui/icons-material';
import './App.css';

// Auth state type
type AuthState = 'validating' | 'authorized' | 'denied' | 'error' | 'no_token';

function App() {
  const [locations, setLocations] = useState<CommunityLocation[]>([]);
  const [allLocations, setAllLocations] = useState<CommunityLocation[]>([]); // Store all initial locations for care level extraction
  const [corporationCarelevels, setCorporationCarelevels] = useState<string[]>([]); // Store care levels from corporation API
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<any>({});
  const [hasMoreData, setHasMoreData] = useState(true);
  const [currentLimit, setCurrentLimit] = useState(1000);
  const mapRef = useRef<any>(null);
  
  // Auth state
  const [authState, setAuthState] = useState<AuthState>('validating');
  const [accessDeniedMessage, setAccessDeniedMessage] = useState<string>('');

  // Parse URL parameters for all filters including user_token
  const getFiltersFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const filters: any = {};
    
    const corporationId = urlParams.get('corporation_id');
    if (corporationId) filters.corporation_id = corporationId;
    
    const communityId = urlParams.get('community_id');
    if (communityId) filters.community_id = communityId;
    
    const careLevel = urlParams.get('care_level');
    if (careLevel) filters.care_level = careLevel;
    
    const limit = urlParams.get('limit');
    if (limit && !isNaN(Number(limit))) filters.limit = Number(limit);
    
    return filters;
  }, []);

  // Get user_token from URL
  const getUserTokenFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('user_token');
  }, []);

  // Extract unique care levels from all locations OR from specific corporation
  const availableCarelevels = useMemo(() => {
    // If we have a corporation filter, use care levels from corporation API
    if (currentFilters.corporation_id && corporationCarelevels.length > 0) {
      return corporationCarelevels.sort();
    }
    
    // Otherwise, extract from all locations
    const careLevels = new Set<string>();
    allLocations.forEach(location => {
      if (location.care_level && Array.isArray(location.care_level)) {
        location.care_level.forEach(level => careLevels.add(level));
      }
    });
    return Array.from(careLevels).sort();
  }, [allLocations, currentFilters.corporation_id, corporationCarelevels]);

  // Handle care level filter changes
  const handleCareLevelChange = useCallback(async (careLevel: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Build new filters
      const urlFilters = getFiltersFromURL();
      const newFilters = { ...urlFilters };
      
      if (careLevel) {
        newFilters.care_level = careLevel;
        // Update URL with new care level
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('care_level', careLevel);
        window.history.pushState({}, '', newUrl.toString());
      } else {
        delete newFilters.care_level;
        // Remove care_level from URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('care_level');
        window.history.pushState({}, '', newUrl.toString());
      }
      
      const initialLimit = newFilters.limit || 100;
      setCurrentFilters(newFilters);
      setCurrentLimit(initialLimit);
      setHasMoreData(true);
      
      const data = await geoAPI.getLocations({ ...newFilters, limit: initialLimit });
      setLocations(data.locations);
      
      // Check if we got less data than requested
      if (data.locations.length < initialLimit) {
        setHasMoreData(false);
      }
    } catch (err) {
      console.error('Error applying care level filter:', err);
      setError('Failed to apply filter. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [getFiltersFromURL]);

  // Load corporation care levels when corporation_id is present
  const loadCorporationCarelevels = useCallback(async (corporationId: string) => {
    try {
      const corporationData = await geoAPI.getCorporation(corporationId);
      setCorporationCarelevels(corporationData.care_levels || []);
    } catch (err) {
      console.error('Error loading corporation care levels:', err);
      setCorporationCarelevels([]);
    }
  }, []);

  // Initialize filters from URL on component mount
  useEffect(() => {
    const urlFilters = getFiltersFromURL();
    setCurrentFilters(urlFilters);
  }, [getFiltersFromURL]);

  // Validate token and load initial data
  useEffect(() => {
    const validateAndLoadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get filters and token from URL
        const urlFilters = getFiltersFromURL();
        const userToken = getUserTokenFromURL();
        
        // user_token is always required
        if (!userToken) {
          console.error('No user_token provided');
          setAuthState('no_token');
          setAccessDeniedMessage('Authentication token is required to access this application. Please ensure you are accessing this page through the authorized application.');
          setLoading(false);
          return;
        }
        
        // If there's a corporation_id or community_id, validate access
        if (urlFilters.corporation_id || urlFilters.community_id) {
          setAuthState('validating');
          try {
            await geoAPI.validateAccess({
              corporationId: urlFilters.corporation_id,
              communityId: urlFilters.community_id,
              userToken
            });
            setAuthState('authorized');
            console.log('Token validated successfully');
          } catch (validationError) {
            const error = validationError as TokenValidationError;
            console.error('Token validation failed:', error);
            
            if (error.isAccessDenied) {
              setAuthState('denied');
              setAccessDeniedMessage(error.message || 'You do not have access to this data.');
            } else if (error.status === 401) {
              setAuthState('denied');
              setAccessDeniedMessage('Your authentication token is invalid or has expired. Please log in again through the main application.');
            } else {
              setAuthState('error');
              setAccessDeniedMessage(error.message || 'An error occurred while validating your access.');
            }
            setLoading(false);
            return;
          }
          
          // Load corporation care levels after successful validation (if corporation_id present)
          if (urlFilters.corporation_id) {
            await loadCorporationCarelevels(urlFilters.corporation_id);
          }
        } else {
          // No corporation filter - only ADMIN users can see all data
          setAuthState('validating');
          try {
            // Validate token - backend will reject non-admin users
            await geoAPI.validateToken(userToken);
            setAuthState('authorized');
            console.log('Token validated successfully - user is admin');
          } catch (validationError) {
            const error = validationError as TokenValidationError;
            console.error('Token validation failed:', error);
            
            if (error.isAccessDenied || error.status === 403) {
              setAuthState('denied');
              setAccessDeniedMessage('You do not have admin privileges to view all data. Please access this application with a specific corporation_id parameter.');
            } else if (error.status === 401) {
              setAuthState('denied');
              setAccessDeniedMessage('Your authentication token is invalid or has expired. Please log in again through the main application.');
            } else {
              setAuthState('error');
              setAccessDeniedMessage(error.message || 'An error occurred while validating your access.');
            }
            setLoading(false);
            return;
          }
          
          // Load ALL locations without filters to get care levels (only for admins)
          const allData = await geoAPI.getLocations({ limit: 10000 });
          setAllLocations(allData.locations);
        }
        
        const initialFilters = { ...urlFilters, limit: urlFilters.limit || 100 };
        
        // Load filtered locations based on URL
        const [locationsData] = await Promise.all([
          geoAPI.getLocations(initialFilters)
        ]);
        
        setLocations(locationsData.locations);
        setCurrentFilters(initialFilters);
        setCurrentLimit(initialFilters.limit);
        
        // Check if we have more data available
        if (locationsData.locations.length < initialFilters.limit) {
          setHasMoreData(false);
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Failed to load data. Please check if the backend is running.');
        setAuthState('error');
      } finally {
        setLoading(false);
      }
    };

    validateAndLoadData();
  }, [getFiltersFromURL, getUserTokenFromURL, loadCorporationCarelevels]);

  // Handle filter changes from control panel
  // Load more data for infinite scrolling
  const loadMoreData = useCallback(async () => {
    if (loadingMore || !hasMoreData) return;
    
    try {
      setLoadingMore(true);
      const newLimit = Math.min(currentLimit + 500, 5000); // Load 500 more items, max 5000 total
      
      // If we've reached the maximum limit, don't load more
      if (currentLimit >= 5000) {
        setHasMoreData(false);
        setLoadingMore(false);
        return;
      }
      
      setCurrentLimit(newLimit);
      
      const data = await geoAPI.getLocations({ ...currentFilters, limit: newLimit });
      
      // If we got the same amount of data or less than expected, we've reached the end
      if (data.locations.length <= locations.length || data.locations.length < newLimit) {
        setHasMoreData(false);
      }
      
      setLocations(data.locations);
    } catch (err) {
      console.error('Error loading more data:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMoreData, currentLimit, currentFilters, locations.length]);

  // Show access denied page if auth failed
  if (authState === 'denied' || authState === 'no_token') {
    return (
      <AccessDenied 
        message={accessDeniedMessage}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // Show loading while validating
  if (authState === 'validating') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          textAlign: 'center'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e0e0e0',
            borderTopColor: '#2196F3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#666', fontSize: '16px' }}>Validating access...</p>
          <style>
            {`@keyframes spin { to { transform: rotate(360deg); } }`}
          </style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <h2 style={{ color: '#f44336', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
            <Warning /> Connection Error
          </h2>
          <p style={{ marginBottom: '20px', color: '#666' }}>{error}</p>
          <p style={{ fontSize: '14px', color: '#999' }}>
            Make sure the FastAPI backend is running on http://localhost:8000
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              marginTop: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '16px auto 0'
            }}
          >
            <Refresh /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Map */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          height: '80vh',
          minHeight: '600px'
        }}>
          <GeospatialMap
            ref={mapRef}
            locations={locations}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={loadMoreData}
            hasMoreData={hasMoreData}
            availableCarelevels={availableCarelevels}
            currentCareLevel={currentFilters.care_level || ''}
            onCareLevelChange={handleCareLevelChange}
          />
        </div>

        {/* Footer Info */}
        <div style={{
          marginTop: '20px',
          padding: '16px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          fontSize: '14px',
          color: '#666',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '8px' }}>
            <strong>Showing {locations.length.toLocaleString()} locations</strong>
          </div>
          
          {/* Show active filters from URL */}
          {Object.keys(currentFilters).filter(key => key !== 'limit').length > 0 && (
            <div style={{ marginBottom: '8px', fontSize: '13px' }}>
              <strong>Active Filters:</strong>{' '}
              {currentFilters.corporation && (
                <span style={{ 
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 8px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '11px'
                }}>
                  Corporation: {currentFilters.corporation}
                </span>
              )}
              {currentFilters.care_level && (
                <span style={{ 
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 8px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '11px'
                }}>
                  Care Level: {currentFilters.care_level}
                </span>
              )}
              {currentFilters.limit && currentFilters.limit !== 100 && (
                <span style={{ 
                  display: 'inline-block',
                  marginLeft: '8px',
                  padding: '2px 8px',
                  backgroundColor: '#FF9800',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '11px'
                }}>
                  Limit: {currentFilters.limit}
                </span>
              )}
            </div>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
            <Analytics fontSize="small" /> 
            {Object.keys(currentFilters).filter(key => key !== 'limit').length > 0 
              ? 'Filtered view based on URL parameters' 
              : 'Data includes care communities across multiple corporations and care levels'
            }
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
